import { IQuestionConfig } from '../../../ui-testrunner/models';
import { EFilterMethods } from '../../widget-audits/data/audits';
import {
  checkPropCondVal,
  getQuestionRespondableDeep,
} from '../../widget-audits/util/content-analysis';

/** Passage / instruction items with no response entries — exclude from panel op/FT totals. */
export function isRespondablePanelQuestion(question: IQuestionConfig | null | undefined): boolean {
  if (!question?.content) {
    return false;
  }
  return getQuestionRespondableDeep(question).length > 0;
}

export function isReadingSelectionPanelQuestion(
  question: IQuestionConfig | null | undefined,
  isReadingSelectionPage?: boolean,
): boolean {
  if (!question) {
    return false;
  }
  return !!(isReadingSelectionPage ?? question.isReadingSelectionPage);
}

/** Whether an item contributes to assembled-panel Operational / Field Test columns. */
export function shouldCountInPanelOpFtTotals(
  question: IQuestionConfig | null | undefined,
  options: { isReadingSelectionPage?: boolean } = {},
): boolean {
  if (!question) {
    return false;
  }
  if (isReadingSelectionPanelQuestion(question, options.isReadingSelectionPage)) {
    return false;
  }
  return isRespondablePanelQuestion(question);
}

export type PanelQuestionFtBucket = 'skip' | 'fieldTest' | 'operational';

export function classifyPanelQuestionFtBucket(
  question: IQuestionConfig | null | undefined,
  options: { isReadingSelectionPage?: boolean } = {},
): PanelQuestionFtBucket {
  if (!shouldCountInPanelOpFtTotals(question, options)) {
    return 'skip';
  }
  const isFieldTest = checkPropCondVal(question.meta?.['FT'], EFilterMethods.IS_TRUTHY);
  return isFieldTest ? 'fieldTest' : 'operational';
}

export function countPanelOpFtFromQuestions(
  questions: IQuestionConfig[],
  options: { resolveReadingSelection?: (q: IQuestionConfig) => boolean } = {},
): { operationalCount: number; fieldTestCount: number; otherPagesCount: number } {
  const questionCount = { fieldTestCount: 0, operationalCount: 0, otherPagesCount: 0 };
  for (const q of questions || []) {
    const isReadingSelectionPage = options.resolveReadingSelection?.(q);
    const bucket = classifyPanelQuestionFtBucket(q, { isReadingSelectionPage });
    if (bucket === 'fieldTest') {
      questionCount.fieldTestCount++;
    } else if (bucket === 'operational') {
      questionCount.operationalCount++;
    } else {
      questionCount.otherPagesCount++;
    }
  }
  return questionCount;
}
